import { Practitioner } from '../../../models/Entities/Practitioners/Practitioner';
import { PractitionerSpecialtyAssociation } from '../../../models/Entities/PractitionerAssociations/Specialties/PractitionerSpecialtyAssociation';
import { SpecialtyViewModel } from '../../../models/Entities/Specialties/SpecialtyModels';

/**
 * Cruce de las tres cargas independientes del listado de practitioners:
 * - los practitioners (Identity),
 * - todas las asociaciones médico / especialidad (PractitionerAssociation.API),
 * - el catálogo de especialidades (nombres).
 * Se aplica en el reductor cada vez que llega cualquiera de las tres, así el orden de llegada no importa.
 */

const key = (id: string | null | undefined): string => (id ?? '').toLowerCase();

/** Asociaciones con su especialidad resuelta por id (cuando el catálogo ya está). */
export const enrichAssociations = (
  associations: PractitionerSpecialtyAssociation[],
  definitions: SpecialtyViewModel[] | undefined
): PractitionerSpecialtyAssociation[] => {
  if (!definitions || definitions.length === 0) return associations;
  const byId = new Map(definitions.map((definition) => [definition.id, definition]));
  return associations.map((association) => ({
    ...association,
    specialty: byId.get(association.specialtyId) ?? association.specialty,
  }));
};

/**
 * Devuelve el listado con `SpecialtiesAssociated` tomado de `associations` (agrupadas por practitioner, con
 * mayúsculas normalizadas). Sin asociaciones cargadas, el listado queda como está.
 */
export const mergeSpecialties = (
  list: Practitioner[] | undefined,
  associations: PractitionerSpecialtyAssociation[] | undefined,
  definitions: SpecialtyViewModel[] | undefined
): Practitioner[] => {
  const practitioners = list ?? [];
  if (!associations) {
    // Sólo nombres: se enriquecen las asociaciones que cada practitioner ya tenga.
    if (!definitions || definitions.length === 0) return practitioners;
    return practitioners.map((practitioner) =>
      practitioner.SpecialtiesAssociated?.length
        ? { ...practitioner, SpecialtiesAssociated: enrichAssociations(practitioner.SpecialtiesAssociated, definitions) }
        : practitioner
    );
  }
  const grouped = new Map<string, PractitionerSpecialtyAssociation[]>();
  enrichAssociations(associations, definitions).forEach((association) => {
    const bucket = grouped.get(key(association.practitionerId)) ?? [];
    bucket.push(association);
    grouped.set(key(association.practitionerId), bucket);
  });
  return practitioners.map((practitioner) => ({
    ...practitioner,
    SpecialtiesAssociated: grouped.get(key(practitioner.userId)) ?? [],
  }));
};
